import React, { useMemo, useCallback, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AddEditBusDialog from "../dialogs/AddEditBusDialog";
import CopyBusDialog from "../dialogs/CopyBusDialog";
import {
  saveTransportGroup,
  deleteTransportGroup,
  updateTransportGroupPassengers,
  getTransportErrorMessage,
} from "../../api/transportBoardApi";
import { useAirports } from "../../../Transfers/hooks/useAirports";
import {
  buildFilteredJourneys,
  buildAllocatedKeysByJourneyType,
  buildBusesByJourneyType,
  buildFilteredCandidateRows,
  buildCandidateGroups,
  buildCandidateGroupsWithCoverage,
  buildSelectedCampusLabel,
  buildMissingPassengersForGroup,
  buildAdditionalPassengersForBus,
  limitPassengersToCapacity,
  buildPassengerPayload,
  buildJourneyUpdatePayload,
} from "../../utils/transportBoardUtils";
import { can } from "../../../shared utils/auth";
import { useCandidateGroups } from "../../hooks/useCandidateGroups";
import { useBusDialog } from "../../hooks/useBusDialog";
import CandidateGroupsPanel from "./CandidateGroupsPanel";
import JourneysPanel from "./JourneysPanel";

export default function CampusTransportBoard({
  campuses = [],
  campusesLoading,
  journeys = [],
  loading,
  error,
  selectedDate,
  setSelectedDate,
  selectedCampusId,
  setSelectedCampusId,
  onRefresh,
  showToast,
}) {
  const campDateStr = useMemo(
    () => (selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : null),
    [selectedDate]
  );

  const [copyBusOpen, setCopyBusOpen] = useState(false);

  // Reading transport is USER; creating, editing, re-routing, confirming and
  // seating a bus need the TRANSPORT grant, which the server enforces. The
  // controls go rather than being offered and refused.
  const canEdit = can("transport");

  const campusList = useMemo(
    () => (campuses || []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [campuses]
  );

  const { airports } = useAirports({ activeOnly: true });

  const {
    candidateRows,
    candidatesLoading,
    candidatesError,
    reloadCandidateGroups,
  } = useCandidateGroups(campDateStr);

  const {
    busDialogOpen,
    editingJourney,
    openAddBus,
    openEditBus,
    closeBusDialog,
  } = useBusDialog();

  const filteredJourneys = useMemo(
    () => buildFilteredJourneys({ journeys, campusList, selectedCampusId }),
    [journeys, campusList, selectedCampusId]
  );

  const allocatedKeysByJourneyType = useMemo(
    () => buildAllocatedKeysByJourneyType(filteredJourneys),
    [filteredJourneys]
  );

  const busesByJourneyType = useMemo(
    () => buildBusesByJourneyType(filteredJourneys),
    [filteredJourneys]
  );

  const refreshBoard = useCallback(async () => {
    await onRefresh?.();
    await reloadCandidateGroups();
  }, [onRefresh, reloadCandidateGroups]);

  const assignSelectedToBus = async ({ busId, group, selectedPeople }) => {
    if (!Array.isArray(selectedPeople) || selectedPeople.length === 0) {
      showToast?.("No travellers selected.", "info");
      return;
    }

    const bus = filteredJourneys.find((j) => Number(j.id) === Number(busId));
    if (!bus) {
      showToast?.("Bus not found.", "error");
      return;
    }

    const toAdd = buildAdditionalPassengersForBus({
      bus,
      missingPeople: selectedPeople,
    });

    const { addNow } = limitPassengersToCapacity({
      bus,
      passengersToAdd: toAdd,
    });

    if (addNow.length === 0) {
      showToast?.("No capacity on this bus.", "warning");
      return;
    }

    const existing = Array.isArray(bus.passengers) ? bus.passengers : [];

    try {
      await updateTransportGroupPassengers(busId, [
        ...buildPassengerPayload(existing),
        ...addNow,
      ]);

      const notAdded = selectedPeople.length - addNow.length;

      if (notAdded > 0) {
        showToast?.(
          `Assigned ${addNow.length} passenger(s). ${notAdded} could not be added due to capacity.`,
          "warning"
        );
      } else {
        showToast?.(`Assigned ${addNow.length} passenger(s).`, "success");
      }

      await refreshBoard();
    } catch (e) {
      showToast?.(getTransportErrorMessage(e, "Assign failed"), "error");
    }
  };

  const handleSaveBus = async ({ mode, id, payload }) => {
    try {
      const result = await saveTransportGroup({ mode, id, payload });

      if (mode === "add") {
        showToast?.("Bus created", "success");
        closeBusDialog();
        await refreshBoard();
        return result;
      }

      showToast?.(
        result?.confirmed
          ? "Bus confirmed"
          : result?.unconfirmed
          ? "Bus unconfirmed"
          : "Bus updated",
        "success"
      );
      closeBusDialog();
      await refreshBoard();
      return true;
    } catch (e) {
      console.error("Save bus failed:", e);
      showToast?.(getTransportErrorMessage(e, "Save failed"), "error");
      throw e;
    }
  };

  const selectedCampusLabel = useMemo(
    () => buildSelectedCampusLabel({ selectedCampusId, campusList }),
    [selectedCampusId, campusList]
  );

  const handleDeleteJourney = async (journey) => {
    await deleteTransportGroup(journey.id);
    showToast?.("Bus deleted", "success");
    await refreshBoard();
  };

  const filteredCandidateRows = useMemo(
    () =>
      buildFilteredCandidateRows({
        candidateRows,
        selectedCampusId,
        campusList,
      }),
    [candidateRows, selectedCampusId, campusList]
  );

  const candidateGroups = useMemo(
    () => buildCandidateGroups(filteredCandidateRows),
    [filteredCandidateRows]
  );

  const candidateGroupsWithCoverage = useMemo(
    () =>
      buildCandidateGroupsWithCoverage({
        candidateGroups,
        filteredJourneys,
        selectedCampusId,
      }),
    [candidateGroups, filteredJourneys, selectedCampusId]
  );

  const assignMissingToBus = async ({ busId, group }) => {
    const missing = buildMissingPassengersForGroup(group);

    if (missing.length === 0) {
      showToast?.("No unallocated travellers in this group.", "info");
      return;
    }

    const bus = filteredJourneys.find((j) => Number(j.id) === Number(busId));
    if (!bus) {
      showToast?.("Bus not found.", "error");
      return;
    }

    const toAdd = buildAdditionalPassengersForBus({
      bus,
      missingPeople: missing,
    });

    const { addNow } = limitPassengersToCapacity({
      bus,
      passengersToAdd: toAdd,
    });

    if (addNow.length === 0) {
      showToast?.("No capacity on this bus.", "warning");
      return;
    }

    const existing = Array.isArray(bus.passengers) ? bus.passengers : [];

    try {
      await updateTransportGroupPassengers(busId, [
        ...buildPassengerPayload(existing),
        ...addNow,
      ]);

      showToast?.(`Assigned ${addNow.length} passenger(s).`, "success");
      await refreshBoard();
    } catch (e) {
      showToast?.(getTransportErrorMessage(e, "Assign failed"), "error");
    }
  };

  const savePassengersForBus = async (busId, nextPassengers) => {
    await updateTransportGroupPassengers(busId, nextPassengers);
    await refreshBoard();
  };

  const confirmJourney = async (journey, { contactName, contactNumber }) => {
    try {
      await saveTransportGroup({
        mode: "edit",
        id: journey.id,
        payload: buildJourneyUpdatePayload(journey, {
          contactName,
          contactNumber,
          confirm: true,
        }),
      });
      showToast?.("Bus confirmed", "success");
      await refreshBoard();
    } catch (e) {
      showToast?.(getTransportErrorMessage(e, "Confirm failed"), "error");
      throw e;
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <DatePicker
            label="Date"
            value={selectedDate?.toDate?.() ? selectedDate.toDate() : selectedDate}
            onChange={(d) => d && setSelectedDate(dayjs(d))}
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>

        <FormControl size="small" sx={{ minWidth: 220 }} disabled={campusesLoading}>
          <InputLabel>Campus</InputLabel>
          <Select
            label="Campus"
            value={selectedCampusId ?? ""}
            onChange={(e) => setSelectedCampusId(e.target.value)}
          >
            <MenuItem value="">
              <em>Select campus…</em>
            </MenuItem>
            {campusList.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem value="UNASSIGNED">Unassigned</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={async () => {
            await refreshBoard();
          }}
          disabled={loading}
        >
          Refresh
        </Button>

        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddBus}
            disabled={loading || !campDateStr}
          >
            Add bus
          </Button>
        )}

        {canEdit && (
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => setCopyBusOpen(true)}
            disabled={loading || !campDateStr}
          >
            Copy bus
          </Button>
        )}

        {loading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2">Loading…</Typography>
          </Stack>
        )}

        {error && (
          <Typography variant="body2" color="error">
            {String(error?.message || error)}
          </Typography>
        )}
      </Stack>

      <AddEditBusDialog
        open={busDialogOpen}
        onClose={closeBusDialog}
        campDate={campDateStr}
        campuses={campusList}
        servedAirports={airports}
        defaultCampusId={selectedCampusId}
        journey={editingJourney}
        onSave={handleSaveBus}
      />

      <CopyBusDialog
        open={copyBusOpen}
        onClose={() => setCopyBusOpen(false)}
        targetDate={campDateStr}
        onCopied={async () => {
          await refreshBoard();
        }}
      />

      {campusesLoading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading campuses…</Typography>
        </Stack>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CandidateGroupsPanel
              selectedCampusId={selectedCampusId}
              selectedCampusLabel={selectedCampusLabel}
              candidateGroups={candidateGroups}
              candidateGroupsWithCoverage={candidateGroupsWithCoverage}
              candidatesLoading={candidatesLoading}
              candidatesError={candidatesError}
              allocatedKeysByJourneyType={allocatedKeysByJourneyType}
              busesByJourneyType={busesByJourneyType}
              onAssignMissing={canEdit ? assignMissingToBus : undefined}
              onAssignSelected={canEdit ? assignSelectedToBus : undefined}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <JourneysPanel
              selectedCampusId={selectedCampusId}
              selectedCampusLabel={selectedCampusLabel}
              filteredJourneys={filteredJourneys}
              onEditJourney={canEdit ? openEditBus : undefined}
              onDeleteJourney={canEdit ? handleDeleteJourney : undefined}
              onSavePassengers={canEdit ? savePassengersForBus : undefined}
              onConfirmJourney={canEdit ? confirmJourney : undefined}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}